import { useEffect, useMemo, useState } from 'react';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import DialogStandard from '@bumble/app/components/DialogStandard';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import { Loading } from '@bumble/app/components/Loading';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { useApi } from '@bumble/app/hooks/use-api';
import { Card, CardContent, Chip, Tooltip } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { TicketDetail, Ticket, TicketFile } from '@bumble/app/__generated__/Api';
import { CaseDetails, CreateCase, CorrespondenceDialog } from '@bumble/app/components/support-cases';
import SplitPanes from '../components/SplitPanes';
import MultipleSelections from '../components/MultipleSelections';
import { apiError } from '@bumble/app/utils/api-error';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import SupportTabs from '../components/support-cases/SupportTabs';

const columns: GridColDef[] = [
  { field: 'ticket_id', headerName: 'Ticket ID', width: 200, disableColumnMenu: true },
  { field: 'title', headerName: 'Subject', width: 150, disableColumnMenu: true },
  { field: 'priority', headerName: 'Priority', width: 150, disableColumnMenu: true },
  { field: 'category', headerName: 'Category', width: 150, disableColumnMenu: true },
  { field: 'state', headerName: 'Status', width: 150, disableColumnMenu: true },
  {
    field: 'uploaded_files',
    headerName: 'Attachments',
    width: 150,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ value }: { value?: TicketFile[] }) => {
      if (!value?.length) return null;
      return (
        <Tooltip title={value.map((file) => file.filename).join(', ')}>
          <Chip icon={<AttachFileIcon fontSize="small" />} label={value.length} size="small" variant="outlined" />
        </Tooltip>
      );
    },
  },
  {
    field: 'created_at',
    headerName: 'Created',
    width: 300,
    disableColumnMenu: true,
  },
];

const columnVisible = {
  ticket_id: true,
  title: true,
  priority: true,
  state: true,
  uploaded_files: true,
  created_at: true,
};

export const Support = () => {
  const api = useApi();

  const [showCaseCreate, setShowCaseCreate] = useState(false);
  const [showCorrespondenceDialog, setShowCorrespondenceDialog] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [detailData, setDetailData] = useState<TicketDetail | undefined>(undefined);
  const [showConfirmClose, setShowConfirmClose] = useState<boolean>(false);

  const selectedRowItem = useMemo(
    () => (rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : ''),
    [rowSelectionModel]
  );

  const {
    isLoading: isLoadingTickets,
    data: ticketsResponse,
    refetch: refetchTickets,
  } = useQuery(['tickets'], api.getTicketApiV1GetTicketGet.bind(null, {}), { enabled: true });

  const {
    isLoading: isLoadingDetail,
    data: ticketDetailResponse,
    refetch: refetchTicketDetail,
  } = useQuery(
    ['ticket-detail', selectedRowItem],
    () => api.getTicketDetailApiV1GetTicketDetailGet({ ticket_id: selectedRowItem }),
    { enabled: Boolean(selectedRowItem) }
  );

  useEffect(() => {
    if (selectedRowItem) {
      setSelectedItemId(selectedRowItem);
      setIsReplying(false);
    } else {
      setSelectedItemId('');
      setDetailData(undefined);
    }
  }, [selectedRowItem]);

  useEffect(() => {
    if (ticketDetailResponse?.data) {
      setDetailData(ticketDetailResponse.data as TicketDetail);
    } else if (!selectedRowItem) {
      setDetailData(undefined);
    }
  }, [ticketDetailResponse?.data, selectedRowItem]);

  const filteredRows = ticketsResponse?.data as Ticket[] || [];

  const closeTicket = useMutation(api.closeTicketApiV1CloseTicketPost, {
    onSuccess() {
      refetchTickets();
      refetchTicketDetail();
      toast.success(`Closed the ticket successfully.`);
    },
    onError: apiError,
  });

  const handleCloseTicket = () => {
    if (!selectedItemId) {
      toast.info('Please select a ticket to close.');
      return;
    }
    closeTicket.mutate({ ticket_id: selectedItemId });
  };

  const menuItems = [
    {
      label: 'Resolve ticket',
      icon: <DeleteForeverOutlinedIcon fontSize="small" color="error" />,
      onClick: () => setShowConfirmClose(true),
    },
  ];

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection.slice(-1));
  };

  const handleCreateCase = () => {
    setShowCaseCreate(true);
  };

  const handleCloseCorrespondenceDialog = () => {
    setShowCorrespondenceDialog(false);
    setIsReplying(false);
  };

  return (
    <PageContent>
      <PageHeader pageName="Support Center" count={filteredRows.length || 0} />

      {isLoadingTickets ? (
        <Loading />
      ) : (
        <SplitPanes
          top={
            <Card elevation={0}>
              <CardContent>
                <DataTableGrid
                  rows={
                    filteredRows.map((item) => ({
                      ...item,
                      id: item.ticket_id,
                    })) || []
                  }
                  columns={columns}
                  handleSelectionChange={handleSelectionChange}
                  rowSelectionModel={rowSelectionModel}
                  itemsSelected={rowSelectionModel.length === 1}
                  newLabel="Create Ticket"
                  newOnClick={handleCreateCase}
                  columnVisibilityModel={columnVisible}
                  getRowId={(row: Ticket) => row.ticket_id}
                  refreshData={() => {
                    refetchTickets();
                  }}
                  menuItems={menuItems}
                />
              </CardContent>
            </Card>
          }
          bottom={
            <Card elevation={0} sx={{ mt: 5 }}>
              <CardContent>
                {rowSelectionModel.length === 1 ? (
                  isLoadingDetail ? (
                    <Loading />
                  ) : detailData ? (
                    <SupportTabs
                      tabs={[
                        {
                          el: (
                            <CaseDetails
                              data={detailData}
                              onRefetchDetail={refetchTicketDetail}
                              onReplySent={() => {
                                refetchTickets();
                                refetchTicketDetail();
                              }}
                            />
                          ),
                          label: 'Correspondence',
                          id: 0,
                        },
                      ]}
                    />
                  ) : (
                    <MultipleSelections count={rowSelectionModel.length} />
                  )
                ) : (
                  <MultipleSelections count={rowSelectionModel.length} />
                )}
              </CardContent>
            </Card>
          }
        />
      )}

      {showConfirmClose && (
        <DialogConfirm
          open={showConfirmClose}
          handleClose={() => setShowConfirmClose(false)}
          handleConfirm={handleCloseTicket}
          title="Close the ticket"
          description={`Are you certain you wish to proceed with closing ${selectedItemId}? Please note that this action is irreversible.`}
        />
      )}

      {showCaseCreate && (
        <DialogStandard open={showCaseCreate} handleClose={() => setShowCaseCreate(false)} title="Create Ticket">
          <CreateCase
            onSubmit={() => {
              setShowCaseCreate(false);
              refetchTickets();
            }}
          />
        </DialogStandard>
      )}

      {showCorrespondenceDialog && (
        <DialogStandard
          open={showCorrespondenceDialog}
          handleClose={handleCloseCorrespondenceDialog}
          title='Correspondence'
        >
          <CorrespondenceDialog
            ticketID={selectedItemId}
            isReplying={isReplying}
            onReplySent={() => {
              setIsReplying(false);
              refetchTickets();
              refetchTicketDetail();
            }}
            onClose={handleCloseCorrespondenceDialog}
          />
        </DialogStandard>
      )}
    </PageContent>
  );
};

export default Support;
